import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import ObliqueIcon from '@/components/icons/ObliqueIcon';
import { useCompany } from '@/contexts/CompanyContext';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/core/oblique-ui';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * Oblique Design System — AppLauncher
 * ═══════════════════════════════════════════════════════════════════════════
 */

const PANEL_VARIANTS = {
    initial: { opacity: 0, y: 12, scale: 0.95 },
    animate: { opacity: 1, y: 0, scale: 1 },
    exit:    { opacity: 0, y: 12, scale: 0.95 },
};
const PANEL_TRANSITION = { type: "spring", damping: 25, stiffness: 350 };

const modules = [
    { id: 'crm', name: 'CRM', path: '/crm', icon: 'avatar', color: '#6366f1', desc: 'Operations' },
    { id: 'analytics', name: 'Analytics', path: '/analytics', icon: 'bar-chart', color: '#8b5cf6', desc: 'Intelligence' },
    { id: 'quality', name: 'Quality', path: '/quality', icon: 'verified', color: '#10b981', desc: 'Audit & QA' },
    { id: 'lms', name: 'LMS', path: '/lms', icon: '/Apps/LMS.svg', color: '#f59e0b', desc: 'Knowledge' },
    { id: 'automation', name: 'Orbit', path: '/automations', icon: 'bolt', color: '#ec4899', desc: 'Flows' },
    { id: 'ai', name: 'Yoda AI', path: '/ai', icon: 'sparkles', color: '#8b5cf6', desc: 'Intelligence' },
    { id: 'hr', name: 'HR', path: '/hr', icon: 'groups', color: '#f43f5e', desc: 'People' },
    { id: 'finance', name: 'Finance', path: '/finance', icon: 'payments', color: '#14b8a6', desc: 'Capital' },
    { id: 'developer', name: 'Developer', path: '/developer', icon: 'code', color: '#10b981', desc: 'Core OS' }
];

export function AppLauncher() {
    const [isOpen, setIsOpen] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    
    const triggerRef = useRef(null);
    const dropdownRef = useRef(null);
    const [coords, setCoords] = useState({ top: 0, right: 0 });
    
    const { currentCompany } = useCompany();
    const { isSuperadmin } = useAuth();

    // Default to a generous starter set if not specified
    const activeModules = currentCompany?.enabledModules || currentCompany?.active_modules || currentCompany?.activeModules || ['crm', 'quality', 'analytics', 'admin', 'developer', 'ai', 'hr', 'finance'];
    const [upgradeModalMod, setUpgradeModalMod] = useState(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                dropdownRef.current && !dropdownRef.current.contains(event.target) &&
                triggerRef.current && !triggerRef.current.contains(event.target)
            ) {
                setIsOpen(false);
            }
        };
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            
            // Calculate coords
            if (triggerRef.current) {
                const rect = triggerRef.current.getBoundingClientRect();
                setCoords({
                    top: rect.bottom + 12,
                    right: window.innerWidth - rect.right
                });
            }
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    // Update coords on resize/scroll
    useEffect(() => {
        if (!isOpen) return;
        const updateCoords = () => {
            if (triggerRef.current) {
                const rect = triggerRef.current.getBoundingClientRect();
                setCoords({
                    top: rect.bottom + 12,
                    right: window.innerWidth - rect.right
                });
            }
        };
        window.addEventListener('resize', updateCoords);
        window.addEventListener('scroll', updateCoords, true);
        return () => {
            window.removeEventListener('resize', updateCoords);
            window.removeEventListener('scroll', updateCoords, true);
        };
    }, [isOpen]);


    const handleNavigate = (path, isLocked, mod) => {
        if (isLocked) {
            setUpgradeModalMod(mod);
            return;
        }
        setIsOpen(false);
        navigate(path);
    };

    const DropdownContent = (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    ref={dropdownRef}
                    initial="initial" animate="animate" exit="exit"
                    variants={PANEL_VARIANTS} transition={PANEL_TRANSITION}
                    className="fixed w-[calc(100vw-32px)] max-w-[400px] rounded-[28px] z-[2000] p-8 bg-[var(--bg-surface)] border border-[var(--border-strong)] shadow-[0_24px_64px_rgba(0,0,0,0.18)] overflow-hidden"
                    style={{ top: coords.top, right: coords.right }}
                >
                    <div className="text-[11px] font-semibold text-[var(--text-tertiary)] uppercase tracking-[0.12em] mb-6 pl-1 opacity-60">
                        Systems & Modules
                    </div>

                    {/* Staggered Bento Grid */}
                    <div className="grid grid-cols-3 gap-4">
                        {modules.map((mod, i) => {
                            const isActive = location.pathname.startsWith(mod.path === '/' ? '/crm-never' : mod.path) || (mod.path === '/' && location.pathname === '/');
                            const isLocked = !isSuperadmin && mod.id !== 'admin' && !activeModules.includes(mod.id);

                            return (
                                <motion.button
                                    key={mod.id}
                                    initial={{ opacity: 0, scale: 0.8 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ delay: i * 0.05 }}
                                    onClick={() => handleNavigate(mod.path, isLocked, mod)}
                                    className={`flex flex-col items-center justify-center gap-2.5 py-4 px-2 bg-transparent border-none rounded-xl relative transition-transform duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] ${isLocked ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:scale-110'}`}
                                    onMouseEnter={e => {
                                        if (!isLocked) {
                                            const iconDiv = e.currentTarget.querySelector('.app-icon-container');
                                            if (iconDiv) {
                                                iconDiv.style.transform = 'scale(1.15)';
                                                iconDiv.style.filter = `drop-shadow(0 8px 16px ${mod.color}40)`;
                                            }
                                        }
                                    }}
                                    onMouseLeave={e => {
                                        if (!isLocked) {
                                            const iconDiv = e.currentTarget.querySelector('.app-icon-container');
                                            if (iconDiv) {
                                                iconDiv.style.transform = 'scale(1)';
                                                iconDiv.style.filter = 'none';
                                            }
                                        }
                                    }}
                                >
                                    <div 
                                        className="app-icon-container relative flex items-center justify-center w-10 h-10 rounded-xl transition-all duration-200 ease-in-out"
                                        style={{ background: isActive ? `${mod.color}15` : 'transparent' }}
                                    >
                                        {isLocked ? (
                                            <ObliqueIcon name="lock" size={24} color="var(--text-tertiary)" />
                                        ) : (
                                            mod.icon.startsWith('/') ? (
                                                <img 
                                                    src={mod.icon} 
                                                    alt={mod.name} 
                                                    className={`w-8 h-8 object-contain transition-[filter] duration-200 ease-in-out ${isActive ? '' : 'grayscale opacity-80'}`}
                                                />
                                            ) : (
                                                <ObliqueIcon 
                                                    name={mod.icon} 
                                                    size={32} 
                                                    color={isActive ? mod.color : 'var(--text-secondary)'} 
                                                    isActive={isActive}
                                                />
                                            )
                                        )}
                                    </div>
                                    <div className="flex flex-col items-center text-center">
                                        <span 
                                            className="text-[13px] font-medium tracking-[-0.01em]"
                                            style={{ color: isActive ? mod.color : 'var(--text-primary)' }}
                                        >
                                            {mod.name}
                                        </span>
                                        <span className="text-[10px] font-medium text-[var(--text-tertiary)] opacity-70">
                                            {mod.desc}
                                        </span>
                                    </div>
                                    
                                    {/* Active Indicator Glow */}
                                    {isActive && (
                                        <div 
                                            className="absolute bottom-0 left-1/2 -translate-x-1/2 w-6 h-[3px] rounded-t-[3px]"
                                            style={{ background: mod.color, boxShadow: `0 0 10px ${mod.color}` }} 
                                        />
                                    )}
                                </motion.button>
                            );
                        })}
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );

    return (
        <React.Fragment>
            {/* Trigger Button - Refined OS Style */}
            <button
                ref={triggerRef}
                onClick={() => setIsOpen(!isOpen)}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                className={`w-[34px] h-[34px] p-0 rounded-lg border-none outline-none shadow-none cursor-pointer flex items-center justify-center transition-all duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] hover:bg-[var(--bg-hover)] ${isOpen ? 'bg-[var(--bg-subtle)] text-[var(--accent)]' : 'bg-transparent text-[var(--text-secondary)]'}`}
                title="App Launcher"
            >
                <motion.div className="flex items-center justify-center">
                    <svg 
                        width="24" 
                        height="24" 
                        viewBox="0 0 24 24" 
                        fill="currentColor"
                        className={`transition-colors duration-200 ease-in-out ${isOpen ? 'text-[var(--accent)]' : 'text-[var(--text-secondary)]'}`}
                    >
                        {/* Connecting Line from 9th to 1st */}
                        <motion.path
                            d="M 18 18 L 12 18 L 6 18 L 6 12 L 12 12 L 18 12 L 18 6 L 12 6 L 6 6"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            initial={{ pathLength: 0, opacity: 0 }}
                            animate={isHovered || isOpen ? {
                                pathLength: [0, 1],
                                opacity: [0, 1, 1, 0]
                            } : {
                                pathLength: 0,
                                opacity: 0
                            }}
                            transition={{ 
                                pathLength: { duration: 0.5, ease: "easeInOut" },
                                opacity: { duration: 0.6, times: [0, 0.1, 0.8, 1], ease: "easeInOut" }
                            }}
                        />
                        {/* 9 Dots */}
                        {[
                            {cx: 6, cy: 6}, {cx: 12, cy: 6}, {cx: 18, cy: 6},
                            {cx: 6, cy: 12}, {cx: 12, cy: 12}, {cx: 18, cy: 12},
                            {cx: 6, cy: 18}, {cx: 12, cy: 18}, {cx: 18, cy: 18},
                        ].map((d, i) => (
                            <motion.circle 
                                key={i} 
                                cx={d.cx} 
                                cy={d.cy} 
                                r="2.3" 
                                animate={{
                                    scale: isHovered || isOpen ? [1, 1.4, 1] : 1
                                }}
                                transition={{
                                    duration: 0.3,
                                    delay: (isHovered || isOpen) ? (8 - i) * 0.05 : 0
                                }}
                            />
                        ))}
                    </svg>
                </motion.div>
            </button>

            {createPortal(DropdownContent, document.body)}

            {/* Upgrade Required Modal */}
            {typeof document !== 'undefined' && createPortal(
                <AnimatePresence>
                    {upgradeModalMod && (
                        <motion.div 
                            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                            className="fixed inset-0 z-[var(--z-modal-backdrop,5000)] flex items-center justify-center bg-black/40 backdrop-blur-sm"
                            onClick={() => setUpgradeModalMod(null)}
                        >
                            <motion.div 
                                initial={{ scale: 0.95, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0, y: 20 }}
                                onClick={e => e.stopPropagation()}
                                className="bg-[var(--bg-surface)] p-10 rounded-3xl max-w-[400px] w-full border border-[var(--border)] shadow-[0_24px_48px_rgba(0,0,0,0.1)]"
                            >
                                <div 
                                    className="w-12 h-12 rounded-xl flex items-center justify-center mb-6"
                                    style={{ background: `${upgradeModalMod.color}15` }}
                                >
                                    <ObliqueIcon name="workspace_premium" size={24} color={upgradeModalMod.color} />
                                </div>
                                <h2 className="text-[20px] font-semibold text-[var(--text-primary)] mb-2">Upgrade Required</h2>
                                <p className="text-[14px] text-[var(--text-tertiary)] leading-relaxed mb-8">
                                    The <strong>{upgradeModalMod.name}</strong> module is not included in your current active plan. Please upgrade your workspace plan to access these features.
                                </p>
                                <div className="flex gap-3">
                                    <Button onClick={() => setUpgradeModalMod(null)} variant="secondary" className="flex-1 h-10">Cancel</Button>
                                    <Button onClick={() => { setUpgradeModalMod(null); setIsOpen(false); navigate('/admin/billing'); }} variant="primary" className="flex-1 h-10">Upgrade Plan</Button>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>,
                document.body
            )}
        </React.Fragment>
    );
}
